import { formatMoney } from '@scripts/utils/helpers'
import { TieredDiscountTier, TieredDiscount } from '@scripts/utils/tiered-discounts-store'

// Extend Window interface for Shopify theme
declare global {
  interface Window {
    theme: {
      currencySymbol: string
    }
  }
}

interface TieredDiscountsWidgetProps {
  productId: string
  productPrice: number
  productOldPrice?: number | string
  productCollections?: Array<{ id: number }>
  displayMode?: 'full' | 'compact'
}

const TieredDiscountsWidget = ({
  productId,
  productPrice,
  productOldPrice = 0,
  productCollections = [],
  displayMode = 'full'
}: TieredDiscountsWidgetProps) => {
  return {
    // Product data
    productId: String(productId),
    productPriceMoney: Number(productPrice) / 100,
    productOldPriceMoney: Number(productOldPrice) / 100,
    productCollections: productCollections || [],
    displayMode,

    // Discount data
    tieredDiscounts: [] as TieredDiscountTier[],
    selectedTier: null as string | null,
    quantity: 1,
    tieredDiscountsIds: [] as string[],
    tieredDiscountsQtys: [] as number[],

    // Calculated prices
    calculatedPrice: '',
    calculatedOldPrice: '',

    // UI state
    hasDiscounts: false,
    storeReady: false,

    init () {
      console.log('TieredDiscountsWidget init called')
      console.log('Product ID:', this.productId)
      console.log('Product Collections:', this.productCollections)

      // Read collections from script tag if available
      const scriptTagContent = this.$refs.productCollections?.innerText
      if (scriptTagContent) {
        try {
          this.productCollections = JSON.parse(scriptTagContent)
          console.log('Parsed product collections from script tag:', this.productCollections)
        } catch (error) {
          console.error('Error parsing product collections:', error)
          this.productCollections = []
        }
      }

      // Listen for store ready event
      this.waitForStoreReady()
      
      // Always initialize the quantity watcher immediately
      this.initQuantityWatcher()
      
      // Set initial calculated price
      this.updateCalculatedPrice()
    },

    async waitForStoreReady () {
      const store = Alpine.store('tieredDiscounts')

      if (store.isInitialized) {
        // Store is already ready
        this.loadProductDiscounts()
        return
      }

      // Store not ready, wait for progressive updates and ready event
      console.log('Store not ready, waiting for progressive updates and ready event...')

      // Listen for progressive updates to check for this product's discounts early
      const handleProgressiveUpdate = (event: CustomEvent) => {
        const { newDiscounts } = event.detail
        console.log('Progressive update received:', newDiscounts.length, 'new discounts')

        // Check if any of the new discounts are for this product
        const relevantDiscounts = this.checkProductDiscounts(newDiscounts)
        if (relevantDiscounts.length > 0 && !this.hasDiscounts) {
          console.log('Found relevant discounts in progressive update!')
          this.applyProductDiscounts(relevantDiscounts)
        }
      }

      return new Promise<void>((resolve) => {
        const handleStoreReady = () => {
          console.log('Store ready event received')
          document.removeEventListener('tieredDiscountsStore:ready', handleStoreReady)
          document.removeEventListener('tieredDiscountsStore:progressiveUpdate', handleProgressiveUpdate)
          this.loadProductDiscounts()
          resolve()
        }

        document.addEventListener('tieredDiscountsStore:ready', handleStoreReady)
        document.addEventListener('tieredDiscountsStore:progressiveUpdate', handleProgressiveUpdate)
      })
    },

    async loadProductDiscounts () {
      console.log('loadProductDiscounts called')
      if (!this.productId) {
        console.log('No product ID, returning')
        return
      }

      try {
        // Get the store instance
        const store = Alpine.store('tieredDiscounts')
        console.log('Store instance:', store)

        // Store should be ready now
        this.storeReady = true
        console.log('Store discounts length:', store.discounts.length)

        // Get discounts for this product
        const discounts = store.getProductDiscounts(this.productId, this.productCollections)
        console.log('Product discounts found:', discounts)
        console.log('Product discounts length:', discounts.length)

        if (discounts.length > 0) {
          this.tieredDiscounts = this.processDiscounts(discounts)
          this.hasDiscounts = true

          // Set up tier arrays like in product-form.ts
          this.tieredDiscountsIds = this.tieredDiscounts.map(discount => discount.id)
          this.tieredDiscountsQtys = this.tieredDiscounts.map(discount => discount.quantity)

          // Set initial selected tier
          const initialTier = this.tieredDiscounts.find((tier) => tier.quantity >= 1)
          this.selectedTier = initialTier ? initialTier.id : null

          console.log('Processed discounts:', this.tieredDiscounts)
          console.log('Has discounts set to:', this.hasDiscounts)
          this.updateCalculatedPrice()
          this.updateSelectedTier()
          this.initQuantityWatcher()
        } else {
          console.log('No discounts found for this product')
          // Even without discounts, initialize the calculated price
          this.updateCalculatedPrice()
          this.initQuantityWatcher()
        }
      } catch (error) {
        console.error('Error loading product discounts:', error)
        // Still initialize prices even if discounts fail
        this.updateCalculatedPrice()
      } finally {
        // this.isLoading = false // Removed isLoading
        // Always ensure quantity watcher is initialized, even if discounts fail
        this.initQuantityWatcher()
      }
    },

    // Watch for quantity changes to update prices
    initQuantityWatcher () {
      this.$watch('quantity', (value) => {
        if (value === '') {
          this.quantity = 1
        }

        // Always update prices, regardless of whether there are discounts
        this.updateCalculatedPrice()

        this.updateSelectedTier()
      })
    },

    // Check if any discounts in a batch are relevant to this product
    checkProductDiscounts (discounts: TieredDiscount[]): TieredDiscountTier[] {
      const relevantDiscounts: TieredDiscountTier[] = []

      for (const discount of discounts) {
        // Check if this discount applies to this product
        if (this.discountAppliesToProduct(discount)) {
          // Extract tiers from the discount
          if (discount.tiers && discount.tiers.edges) {
            const tiers = discount.tiers.edges.map(edge => edge.node)
            relevantDiscounts.push(...tiers)
          }
        }
      }

      return relevantDiscounts
    },

    // Check if a discount applies to this product
    discountAppliesToProduct (discount: TieredDiscount): boolean {
      // Check if product is directly in the discount
      if (discount.products && discount.products.length > 0) {
        const productIds = discount.products.map(p => p.id)
        // Normalize IDs for comparison (handle both full GID and numeric formats)
        const normalizedProductIds = productIds.map(id => this.normalizeShopifyId(id))
        const normalizedCurrentId = this.normalizeShopifyId(this.productId)

        if (normalizedProductIds.includes(normalizedCurrentId)) {
          return true
        }
      }

      // Check if product is in any of the discount's collections
      if (discount.collections && discount.collections.length > 0 && this.productCollections.length > 0) {
        const discountCollectionIds = discount.collections.map(c => c.id)
        const normalizedDiscountCollectionIds = discountCollectionIds.map(id => this.normalizeShopifyId(id))
        const normalizedCurrentCollectionIds = this.productCollections.map(id => this.normalizeShopifyId(id))

        const hasMatchingCollection = normalizedCurrentCollectionIds.some(collectionId =>
          normalizedDiscountCollectionIds.includes(collectionId)
        )
        if (hasMatchingCollection) {
          return true
        }
      }

      return false
    },

    // Normalize Shopify IDs (extract numeric part from GID format)
    normalizeShopifyId (id: string | number): string {
      if (typeof id === 'number') return id.toString()

      // Handle Shopify GID format: gid://shopify/Product/123456789
      const match = id.match(/gid:\/\/shopify\/[^/]+\/(\d+)/)
      if (match) {
        return match[1] // Return just the numeric part
      }

      // If not GID format, return as-is
      return id
    },

    // Apply product discounts (called when found in progressive updates)
    applyProductDiscounts (discounts: TieredDiscountTier[]) {
      if (discounts.length === 0) return

      this.tieredDiscounts = this.processDiscounts(discounts)
      this.hasDiscounts = true

      // Set up tier arrays like in product-form.ts
      this.tieredDiscountsIds = this.tieredDiscounts.map(discount => discount.id)
      this.tieredDiscountsQtys = this.tieredDiscounts.map(discount => discount.quantity)

      // Set initial selected tier
      const initialTier = this.tieredDiscounts.find((tier) => tier.quantity >= 1)
      this.selectedTier = initialTier ? initialTier.id : null

      console.log('Applied discounts from progressive update:', this.tieredDiscounts)
      this.updateCalculatedPrice()
      this.updateSelectedTier()
      this.initQuantityWatcher()

      // Hide loading message since we found discounts
      this.storeReady = true
    },

    processDiscounts (discounts: TieredDiscountTier[]): TieredDiscountTier[] {
      return discounts.map(tier => {
        let price: number

        if (tier.type === 'default') {
          price = this.productPriceMoney
        } else if (tier.type === 'percent') {
          price = this.productPriceMoney * (1 - (tier.discountAmount / 100))
        } else if (tier.type === 'fixed') {
          price = this.productPriceMoney - tier.discountAmount
        } else {
          price = this.productPriceMoney
        }

        return {
          ...tier,
          price: `${window.theme.currencySymbol}${price.toFixed(2)}`
        }
      })
    },

    updateCalculatedPrice () {
      // Always calculate the base price
      const basePrice = this.quantity * this.productPriceMoney
      this.calculatedPrice = formatMoney(basePrice)

      // If we have tiered discounts, apply them
      if (this.tieredDiscounts.length > 0) {
        let applicableTier = this.tieredDiscounts[0]
        for (const tier of this.tieredDiscounts) {
          if (this.quantity <= tier.quantity) {
            applicableTier = tier
            break
          }
        }

        if (applicableTier) {
          let totalPrice: number

          if (applicableTier.type === 'fixed') {
            totalPrice = basePrice - (applicableTier.discountAmount * this.quantity)
          } else {
            const pricePerUnit = parseFloat(applicableTier.price.replace(window.theme.currencySymbol, ''))
            totalPrice = this.quantity * pricePerUnit
          }

          this.calculatedPrice = formatMoney(totalPrice)

          // Only show compare price if there's an actual discount
          if (totalPrice < basePrice) {
            this.calculatedOldPrice = formatMoney(this.quantity * this.productPriceMoney)
          } else {
            this.calculatedOldPrice = '' // No discount, so no compare price
          }
        } else {
          this.calculatedOldPrice = formatMoney(this.quantity * this.productPriceMoney)
        }
      } else {
        // No discounts, so no compare price
        this.calculatedOldPrice = ''
      }
    },

    updateSelectedTier () {
      // Only update selected tier if we have tiered discounts
      if (this.tieredDiscounts.length > 0) {
        for (const tier of this.tieredDiscounts) {
          if (this.quantity <= tier.quantity) {
            this.selectedTier = tier.id
            break
          }
        }
      }
      // If no discounts, still allow quantity changes but don't update tier
    },

    onQuantityChange () {
      this.updateCalculatedPrice()
      this.updateSelectedTier()
    },

    onTierOptionChange () {
      const selectedTierObject = this.tieredDiscounts.find(discount => discount.id === this.selectedTier)

      if (!selectedTierObject) return

      if (selectedTierObject.type === 'default') {
        this.quantity = 1
      } else {
        const selectedTierIndex = this.tieredDiscounts.findIndex(tier => tier.id === this.selectedTier)

        if (selectedTierIndex > 0) {
          this.quantity = this.tieredDiscounts[selectedTierIndex - 1].quantity + 1
        }
      }

      this.updateCalculatedPrice()
      this.updateSelectedTier()
    },

    onQtyInput (event: Event) {
      const input = event.target as HTMLInputElement
      const value = input.value
      const quantity = parseInt(value, 10)

      if (!isNaN(quantity) && quantity > 0) {
        this.quantity = quantity
        this.updateCalculatedPrice()
        this.updateSelectedTier()
      } else {
        this.quantity = 1
        this.updateCalculatedPrice()
        this.updateSelectedTier()
      }
    },

    onQtyBtnClick (action: 'add' | 'minus') {
      // Ensure quantity is a number
      const currentQty = parseInt(this.quantity.toString(), 10) || 1

      if (action === 'add') {
        let maxQty = 9999 // Default max quantity
        if (this.tieredDiscounts.length > 0) {
          // If we have tiered discounts, use the highest tier quantity as max
          maxQty = this.tieredDiscounts[this.tieredDiscounts.length - 1]?.quantity || 9999
        }
        this.quantity = Math.min(currentQty + 1, maxQty)
      } else {
        this.quantity = Math.max(currentQty - 1, 1)
      }

      this.updateCalculatedPrice()
      this.updateSelectedTier()
    },

    qtyLimiter (value: number) {
      if (this.tieredDiscounts.length > 0) {
        const lastTier = this.tieredDiscounts[this.tieredDiscounts.length - 1]
        return Math.min(value, lastTier.quantity)
      }
      return value
    },

    // Method to get discount badge text
    getDiscountBadgeText () {
      if (!this.tieredDiscounts.length) return ''

      const firstTier = this.tieredDiscounts[0]
      if (firstTier.type === 'percent') {
        return `Save ${firstTier.discountAmount}%`
      } else if (firstTier.type === 'fixed') {
        return `Save ${window.theme.currencySymbol}${firstTier.discountAmount}`
      }

      return 'Bulk Discount'
    },

    // Method to check if product has tiered pricing
    hasTieredPricing () {
      return this.tieredDiscounts.length > 0
    },

    // Method to check if we should show full interface
    shouldShowFullInterface () {
      return this.displayMode === 'full' && this.hasDiscounts
    },

    // Method to check if we should show compact interface
    shouldShowCompactInterface () {
      return this.displayMode === 'compact' && this.hasDiscounts
    }
  }
}

export default TieredDiscountsWidget
